/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type { CSSProperties } from 'react';
import AppLoader from '@/components/feedback/AppLoader';
import { useI18n } from '@/context/I18nContext';
import PlotlyFigure from '@/components/charts/PlotlyFigure';
import type { StoredPlotlyResponse } from '@/types/eeCheck';

interface StoredPlotlyFigureProps {
    errorMessage?: string;
    fillParent?: boolean;
    figure?: StoredPlotlyResponse | null;
    isLoading?: boolean;
    loadingLabel?: string;
    minWidth?: CSSProperties['minWidth'];
}

function StoredPlotlyFigure({
    errorMessage = '',
    fillParent = false,
    figure,
    isLoading = false,
    loadingLabel,
    minWidth,
}: StoredPlotlyFigureProps) {
    const { t } = useI18n();
    const plotClassName = `time-series-api-plot${minWidth ? ' time-series-api-plot--min-width' : ''}`;
    const plotStyle = minWidth ? { minWidth } : undefined;

    if (isLoading) {
        return (
            <div className={`${plotClassName} time-series-api-plot--loading`} style={plotStyle}>
                <AppLoader label={loadingLabel ?? t('Loading Plotly chart...')} />
            </div>
        );
    }

    if (errorMessage) {
        return (
            <div className={plotClassName} style={plotStyle}>
                <div className="time-series-api-plot__message">{errorMessage}</div>
            </div>
        );
    }

    if (!figure) {
        return (
            <div className={plotClassName} style={plotStyle}>
                <div className="time-series-api-plot__message">{t('No Plotly chart available.')}</div>
            </div>
        );
    }

    return (
        <div className={plotClassName} style={plotStyle}>
            <PlotlyFigure figure={figure} fillParent={fillParent} minWidth={minWidth} />
        </div>
    );
}

export default StoredPlotlyFigure;
